/*
 * Copyright (c) 2023 CalAmp Corp.  All Rights Reserved
 */
import {AxiosInstance, AxiosRequestConfig} from 'axios';
import {translate} from '../locale/locale';
import {info} from '../logger/logger';
import {setMaintenanceReadOnlyMode} from '../services/Maintenance';
import {Method, Module} from '../types/services';
import {setAlertsReadOnlyMode} from './Alerts';
import {setArgGISReadOnlyMode} from './ArcGIS';

export type IntercepterTypeParams = {
  client: AxiosInstance,
  restrictions: string[],
  isReadOnly: boolean,
  module: Module
};

/**
 * Provides a utility to to adds the escape charector for the restrction url provided
 *
 * ```typescript
 *  import {escapeRegExpRestrctionURL} from '@ion/core/src/utils/';
 *
 *  escapeRegExpRestrctionURL('/maintenance/schedules/:id') // '\\/maintenance/schedules/:id'
 *  escapeRegExpRestrctionURL('/maintenance/servicelogs') // '\\/maintenance/servicelogs'
 *
 * ```
 */

export const escapeRegExpRestrctionURL = (str: string) => {
  return str.replace(/[\-\[\]\/\{\}\(\)\:\?\.\\\^\$\|]/, '\\$&');
};

/**
 * Provides a utility to to match the restriction string with config url by converting restriction url to regex.
 *
 * ```typescript
 *  import {fuzzyComparison} from '@ion/core/src/utils/';
 *
 *  fuzzyComparison('/maintenance/schedules/1437', '/maintenance/schedules/:id') // true
 *  fuzzyComparison('/maintenance/serviceitems/search', '/maintenance/servicelogs/:id') // false
 *
 * ```
 */
export const fuzzyComparison = (str: string, mask: string, token = '\\w+'): boolean => {
  try {
    return new RegExp(
      `^${escapeRegExpRestrctionURL(mask).replace(/\:id/, token)}$`
    ).test(str);
  } catch (error) {
    error(error);
    return false;
  }
};

/**
 * check and compair if the provided request URL is restricted based on the given list of restricted URLs
 *
 * ```typescript
 *  import {compair} from '@ion/core/src/services/ReadOnlyInterceptor';
 *
 *  const url  = 'https://qa.connect.calamp.com/maintenance/schedules/1437';
 *  const restrictions = ['https://qa.connect.calamp.com/maintenance/schedules/:id'].
 *
 *  // returns true if the request URL matches with provided list of restrctions url
 *  const isMatch = compair(restrictions, url, token);
 *
 * ```
 */
export const compare = (restrictions: string[], url: string, module: string) => {
  const current = new URL(url).pathname;
  const token   = module === Module.MaintenanceModule ? '\\d+' : '\\w+';
  return restrictions.find(item => (
    fuzzyComparison(current, new URL(item).pathname, token)
  ));
};

const validations = {
  [Module.MaintenanceModule] : compare,
  [Module.AlertsModule]      : compare,
  [Module.ArcGISModule]      : (restrictions, url, module) => {
    if (!isNaN(Number(url.split('/').at(-1)))) {
      return compare(restrictions, url, module);
    }
    return restrictions.includes(url);
  }
};

/**
 * Determines if the provided request is restricted based on the given list of restricted urls and module
 *
 * ```typescript
 *  import {isRestricted} from '@ion/core/src/services/ReadOnlyInterceptor';
 *
 *  const axiosConfig  = {method = 'put', url: 'https://qa.connect.calamp.com/maintenance/schedules/1437'};
 *  const restrictions = ['https://qa.connect.calamp.com/maintenance/schedules/:id'].
 *  const module       = 'maintenance'
 *
 *  // returns true if the restrctions url matches with axiosConfig url & the method is not get
 *  const restricted = isRestricted(axiosConfig, restrictions, module);
 *
 * ```
 */
export const isRestricted = ({method, url}: AxiosRequestConfig, restrictions: string[], module: string): boolean => {
  if (method.toLocaleLowerCase() !== Method.get) {
    return validations[module]?.(restrictions, url, module);
  }
  return false;
};

/**
 * applyInterceptor method is invoked by all Service Modules whose Service APIs do not implement permissions validation
 *
 * ```typescript
 *  import {applyInterceptor} from '@ion/core/src/services/ReadOnlyInterceptor';
 *
 *  const client = AxiosInstance;
 *  const restrictions = ['https://qa.connect.calamp.com/maintenance/schedules/:id']
 *
 *  const restricted = applyInterceptor(client, restrictions); // throw error with custom message and status
 *  if the restrctions url matches with AxiosInstance's axiosConfig url & the method is not get
 *
 * ```
 */
export const applyInterceptor = ({
  client,
  restrictions,
  module,
  isReadOnly = false
}: IntercepterTypeParams) => {
  client.interceptors.request.use((config: AxiosRequestConfig) => {
    if (isReadOnly && isRestricted(config, restrictions, module)) {
      info(`Read-Only Mode ${config.method.toUpperCase()} request intercepted for ${config.url}`);
      throw {
        message : translate('common:editors.shared.restrictedOperation'),
        status  : 403
      };
    }
    return config;
  });
};

/**
 * setReadOnlyMode invoked as a delegate upon Features initialization which in turn invokes
 * setReadOnlyMode on each restricted module
 *
 * ```typescript
 *  import {setReadOnlyMode} from '@ion/core/src/services/ReadOnlyInterceptor';
 *
 *  setReadOnlyMode(true); //when true, sets the read only mode true
 *  for all Service Modules(ex : Maintenance.ts, ArcGIS.ts) in the function
 *
 * ```
 */
export const setReadOnlyMode = (isReadOnly = false) => {
  setMaintenanceReadOnlyMode(isReadOnly);
  setArgGISReadOnlyMode(isReadOnly);
  setAlertsReadOnlyMode(isReadOnly);
};
